/** @format */

import { BreakpointsService, UtilityService } from '@shared/data-access/services';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnInit,
  PLATFORM_ID,
  computed,
  effect,
  inject,
  input,
  model,
  signal,
  untracked,
  viewChild,
  viewChildren,
} from '@angular/core';
import { FacetAccordionElement, FacetVO } from '@plp/data-access/interfaces';
import {
  HmfAccordionComponent,
  HmfButtonComponent,
  HmfDrawerComponent,
} from '@dsg-tech/homefield-angular';
import { NgTemplateOutlet, isPlatformBrowser } from '@angular/common';
import {
  filterContainerClearAllClicked,
  selectFeatureFlags,
  selectHasCatBubbles,
  selectProductList,
  selectProductListSEO,
  selectSelectedFilters,
  selectSkeletonLoader,
  selectValidFacetVOs,
} from '@plp/data-access/state';

import { BreakPointNew } from '@shared/data-access/enums';
import { DrawerAction } from '@dsg-tech/homefield-utils';
import { FilterChipsComponent } from '../filter-chips/filter-chips.component';
import { FilterComponent } from '../filter/filter.component';
import { FilterIdPipe } from '../pipes/filter-id.pipe';
import { ProductListFilterDrawerService } from '@plp/data-access/services';
import { ShippingOptionsComponent } from '../shipping-options/shipping-options.component';
import { Store } from '@ngrx/store';

@Component({
  selector: 'plp-components-filter-container',
  templateUrl: './filter-container.component.html',
  styleUrl: './filter-container.component.scss',
  imports: [
    FilterComponent,
    FilterChipsComponent,
    HmfButtonComponent,
    HmfDrawerComponent,
    FilterIdPipe,
    HmfAccordionComponent,
    ShippingOptionsComponent,
    NgTemplateOutlet,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FilterContainerComponent implements OnInit {
  private readonly store = inject(Store);
  private readonly utilityService = inject(UtilityService);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly breakpointsService = inject(BreakpointsService);
  private readonly filterDrawerService = inject(ProductListFilterDrawerService);

  readonly isPlp3Template = input<boolean>(false);
  readonly sameDayDeliveryEnabled = input<boolean>(false);
  readonly skipLinkOpen = model<boolean>(false);

  readonly featureFlags = this.store.selectSignal(selectFeatureFlags);
  readonly showSkeletonLoader = this.store.selectSignal(selectSkeletonLoader);
  readonly productListFacetVOs = this.store.selectSignal(selectValidFacetVOs);
  readonly hasCatBubbles = this.store.selectSignal(selectHasCatBubbles);
  readonly productListSEOData = this.store.selectSignal(selectProductListSEO);
  readonly productListData = this.store.selectSignal(selectProductList);
  private readonly selectedFilters = this.store.selectSignal(selectSelectedFilters);

  readonly isFilterDrawerOpen = this.filterDrawerService.isOpen;
  readonly refinementSource = this.filterDrawerService.refinementSource;

  /*
		This is the list of the filters/facets tab in the left column
		it is used to maintain the status as the filter component goes through the whole lifecycle
		Example: { 'Brand': { open: true, showMore: false} }
		Means the Brand item in the accordion is open but with the complete list collapsed
	*/
  openFacets = signal<Record<string, { open: boolean; showMore: boolean }> | null>(null);

  readonly selectedFilterCount = computed(() => {
    const filters = this.selectedFilters();
    return Object.keys(filters).reduce((count, prop) => count + filters[prop].length, 0);
  });

  readonly BreakPointSizeEnum = BreakPointNew;
  readonly deviceBreakPoint = this.breakpointsService.breakpoint;

  readonly totalProductCount = computed(() => this.productListData()?.searchVO?.totalCount ?? '');

  readonly primaryAction: DrawerAction = {
    text: 'Show',
    variant: 'primary',
    onClick: () => this.closeFilterDrawer(),
  };

  readonly secondaryAction: DrawerAction = {
    text: 'Clear All',
    variant: 'secondary',
    onClick: () => this.removeFilters(),
  };

  readonly filterDrawerActions = computed(() => {
    const filterInitialDrawerActions: DrawerAction[] = [this.secondaryAction, this.primaryAction];

    return filterInitialDrawerActions.map((action, i) =>
      i === 1 ? { ...action, text: `Show ${this.totalProductCount()}` } : action,
    );
  });

  readonly showShippingFilter = computed(() => this.featureFlags().movefulfillmentOptionsToFilters);

  readonly filterDrawer = viewChild(HmfDrawerComponent);
  readonly filterDrawerElement = viewChild(HmfDrawerComponent, { read: ElementRef });
  readonly filterContainerElements = viewChildren('filterContainer', { read: ElementRef });

  constructor() {
    if (isPlatformBrowser(this.platformId)) {
      effect(() => {
        // After render, if the filter drawer is open, set focus to the drawer for accessibility
        if (this.isFilterDrawerOpen()) {
          untracked(() => {
            this.filterDrawer()?.openDrawer();
            this.filterDrawerElement()?.nativeElement?.focus();

            const scrollToFilter = this.filterDrawerService.scrollToFilter();
            if (scrollToFilter) {
              this.onFacetToggled({
                attrName: scrollToFilter.attrName,
                open: true,
                showMore: false,
              });
              this.scrollToFilter(scrollToFilter.attrIdentifier);
            }
          });
        }
      });

      effect(() => {
        // Close drawer when screen resizes from mobile to desktop
        if (this.deviceBreakPoint() > BreakPointNew.MEDIUM) {
          untracked(() => {
            if (this.filterDrawer()?.open) {
              this.filterDrawer()?.closeDrawer();
            }
          });
        }
      });
    }
  }

  scrollToFilter(filterIdentifier: string): void {
    setTimeout(() => {
      const scrollableDrawerElement = document.getElementsByClassName(
        'hmf-drawer-container-content',
      )?.[0];

      const filterToScrollElement = this.filterContainerElements().find((filterAccordion) =>
        filterAccordion?.nativeElement?.classList?.contains(`filter-container-${filterIdentifier}`),
      );

      if (scrollableDrawerElement && filterToScrollElement) {
        const mobileHeaderHeight = 64;
        const offsetTop = filterToScrollElement?.nativeElement?.offsetTop - mobileHeaderHeight;

        scrollableDrawerElement?.scroll({
          top: offsetTop,
          behavior: 'smooth',
        });
      }
    }, 100);
  }

  ngOnInit(): void {
    this.openFacets.set(this.getOpenFacets(this.productListFacetVOs()));
  }

  async removeFilters(): Promise<void> {
    await this.utilityService.yieldTask(() => {
      this.store.dispatch(filterContainerClearAllClicked());
    });
  }

  onCloseFilterDrawer(): void {
    this.filterDrawerService.close();
  }

  closeFilterDrawer(): void {
    this.filterDrawer()?.closeDrawer();
  }

  /**
   * Determines and returns the state of facets (open or closed) based on the provided facets array.
   * If it's not the plp3 template, the first facet (category) gets rendered as bubble and the next 4 facets are open by default, while the rest are closed.
   * If it's plp3 template, only the first facet, category, will be open.
   *
   * @param facets - An array of `FacetVO` objects representing the facets to be processed.
   * @returns A record where each key is a facet attribute name and the value is an object
   * containing the `open` and `showMore` states.
   */
  getOpenFacets(facets: FacetVO[]): Record<string, { open: boolean; showMore: boolean }> {
    const openFacets = {} as Record<string, { open: boolean; showMore: boolean }>;

    facets.forEach((facet, index) => {
      let numberOfOpenFacets = 3;
      // Number of open facets for superfamily and plp3 template
      if (this.hasCatBubbles() && this.productListSEOData() !== null) {
        numberOfOpenFacets = this.isPlp3Template() ? 0 : 4;
      }

      if (index <= numberOfOpenFacets) {
        openFacets[facet.attrName] = { open: true, showMore: false };
      } else {
        openFacets[facet.attrName] = { open: false, showMore: false };
      }
    });

    return openFacets;
  }

  onFacetToggled({ attrName, open, showMore }: FacetAccordionElement) {
    const facets = this.openFacets();
    if (facets) {
      facets[attrName] = { open, showMore };
      this.openFacets.set({ ...facets });
    }
  }

  focusFirstAccordion(): void {
    setTimeout(() => {
      const headers = this.getVisibleAccordionHeaders();
      if (headers.length > 0) {
        (headers[0] as HTMLElement).focus();
      }
    }, 100);
  }

  arrowDownKeyHandler(event: Event): void {
    const headers = this.getVisibleAccordionHeaders();
    if (headers.length === 0) {
      return;
    }

    event.preventDefault();
    const currentIndex = this.findCurrentAccordionIndex(headers, event.target as HTMLElement);
    const nextIndex = currentIndex === -1 ? 0 : (currentIndex + 1) % headers.length;
    (headers[nextIndex] as HTMLElement).focus();
  }

  arrowUpKeyHandler(event: Event): void {
    const headers = this.getVisibleAccordionHeaders();
    if (headers.length === 0) {
      return;
    }

    event.preventDefault();
    const currentIndex = this.findCurrentAccordionIndex(headers, event.target as HTMLElement);

    // When pressing arrow-up from inside a filter option (checkbox, link, size, brand search),
    // stay on the current accordion header instead of jumping to the one above.
    const filterOptionsClasses = [
      'filter-value-count-container',
      'homefield-checkbox-input',
      'homefield-text-link',
      'homefield-actiontext',
      'size-filter',
    ];
    const target = event.target as HTMLElement;
    const isInsideFilterOption = filterOptionsClasses.some((cls) =>
      target.classList?.contains(cls),
    );
    const isFromBrandSearchInput = target.getAttribute('id')?.includes('homefield-textinput');

    if (isInsideFilterOption || isFromBrandSearchInput) {
      if (currentIndex !== -1) {
        (headers[currentIndex] as HTMLElement).focus();
      }
      return;
    }

    const prevIndex =
      currentIndex === -1
        ? headers.length - 1
        : (currentIndex - 1 + headers.length) % headers.length;
    (headers[prevIndex] as HTMLElement).focus();
  }

  private getVisibleAccordionHeaders(): NodeListOf<Element> {
    return this.deviceBreakPoint() <= BreakPointNew.MEDIUM
      ? document.querySelectorAll('.filter-container .hmf-accordion-label-and-control')
      : document.querySelectorAll('.plp-srlp-filters .hmf-accordion-label-and-control');
  }

  /**
   * Finds the index of the accordion header that contains or is the given target element.
   */
  private findCurrentAccordionIndex(headers: NodeListOf<Element>, target: HTMLElement): number {
    for (let i = 0; i < headers.length; i++) {
      if (headers[i] === target) {
        return i;
      }
      const accordion = headers[i].closest('hmf-accordion') ?? headers[i].parentElement;
      if (accordion?.contains(target)) {
        return i;
      }
    }
    return -1;
  }

  onSkipLinkEnter(currentTarget: Event): void {
    // Prevent the click event from being triggered on the "hmf-selector" component when the button is focused.
    currentTarget.preventDefault();

    this.skipLinkOpen.set(false);
    const productCardContent: HTMLDivElement | null = document.querySelector(
      '#plp-container .product-card .swatches-container button',
    );
    if (productCardContent) {
      productCardContent.focus();
    }
  }
}
